import React, { useState, useEffect } from "react";
import "./ProductCard.css";
import { FaRegEdit } from "react-icons/fa";
import { MdDeleteForever } from "react-icons/md";
import toast from "react-hot-toast";

const ProductCard = ({ product, onUpdate, onDelete }) => {
  const { _id, name, description, price, images, attributes, stock, exclusive: initialExclusive, colorGroup } = product;
  const [exclusive, setExclusive] = useState(initialExclusive);
  const [isInputVisible, setIsInputVisible] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleExclusive = async () => {
    if (!couponCode.trim()) {
      toast.error("Please enter a coupon code");
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(
        `https://admin.riymay.com/api/v1/admin/exclusive/product/${_id}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("admintoken")}`,
          },
          body: JSON.stringify({ exclusiveProductCoupon: couponCode }),
        }
      );
      
      const data = await response.json();
      
      if (response.status === 404) {
        toast.error("Coupon not found");
        return;
      }
      
      if (!response.ok) {
        toast.error(data.message || "Failed to update exclusive status");
        return;
      }
      
      if (data.success) {
        setExclusive(data.exclusiveProduct.exclusive);
        localStorage.setItem(`exclusive_${_id}`, JSON.stringify(data.exclusiveProduct.exclusive));
        toast.success("Product exclusivity updated successfully");
        setIsInputVisible(false);
        setCouponCode("");
      }
    } catch (err) {
      console.error("Error updating exclusive status:", err);
      toast.error("Error updating exclusive status");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const storedExclusive = localStorage.getItem(`exclusive_${_id}`);
    if (storedExclusive !== null) {
      setExclusive(JSON.parse(storedExclusive));
    }
  }, [_id]);

  const { url, altText } = images[0] || {};

  const [isExpanded, setIsExpanded] = useState(false);

  const toggleDescription = () => {
    setIsExpanded(!isExpanded);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleExclusive();
    }
  };

  return (
    <div className="product-card" key={_id}>
      <div className="product-card-image">
        {url ? (
          <img src={`https://riymay.com/assets/${url}`} alt={altText || "Product Image"} />
        ) : (
          <div className="no-image">No image available</div>
        )}
        {exclusive && <div className="exclusive-badge">Exclusive</div>}
      </div>
      <div className="product-card-info">
        <h3 className="product-card-name">
          {isExpanded ? name : `${name.slice(0, 30)}${name.length > 30 ? '...' : ''}`}
          {name.length > 30 && (
            <span onClick={toggleDescription} className="show-more-btn">
              {isExpanded ? "" : ""}
            </span>
          )}
        </h3>
        <p className="product-card-description">
          {isExpanded ? description : `${description.slice(0, 80)}${description.length > 80 ? '...' : ''}`}
          {description.length > 80 && (
            <span onClick={toggleDescription} className="show-more-btn">
              {isExpanded ? "Show Less" : "Show More"}
            </span>
          )}
        </p>
     
        <p><span className="product-card-price">Price</span> ₹{price}</p>
        <p className="product-card-sizes">
          <span className="product-card-price">Size</span> {stock.map((item) => `${item.size}: ${item.quantity}`).join(", ")}
        </p>
        <p className="product-card-color">
          <span className="product-card-price">Colors</span> {attributes.color.join(", ")}
        </p>
        {colorGroup && (
          <p className="product-card-color">
            <span className="product-card-price">Color Group</span> {colorGroup}
          </p>
        )}

        <div className="product-card-actions">
          <button
            className="product-card-update-button"
            onClick={() => onUpdate(_id, product)}
          >
            <FaRegEdit /> Edit
          </button>
          <button
            className="product-card-delete-button"
            onClick={() => onDelete(_id)}
          >
            <MdDeleteForever /> Delete
          </button>
          
          <button
            className={`exclusive-button ${exclusive ? "exclusive-active" : ""}`}
            onClick={() => {
              if (exclusive) {
                // If already exclusive, clicking should remove exclusivity
                if (window.confirm("Remove exclusive status from this product?")) {
                  setIsInputVisible(false);
                  setCouponCode("");
                  setExclusive(false);
                  localStorage.removeItem(`exclusive_${_id}`);
                  
                  // Call API to remove exclusivity
                  fetch(`https://admin.riymay.com/api/v1/admin/remove-exclusive/product/${_id}`, {
                    method: "POST",
                    headers: {
                      Authorization: `Bearer ${localStorage.getItem("admintoken")}`,
                    }
                  })
                  .then(response => response.json())
                  .then(data => {
                    if (data.success) {
                      toast.success("Exclusivity removed successfully");
                    } else {
                      toast.error(data.message || "Failed to remove exclusivity");
                    }
                  })
                  .catch(err => {
                    console.error("Error removing exclusivity:", err);
                    toast.error("Error removing exclusivity");
                  });
                }
              } else {
                // If not exclusive, show input for coupon
                setIsInputVisible(prev => !prev);
              }
            }}
          >
            {exclusive ? "Remove Exclusive" : "Make Exclusive"}
          </button>
        </div>

        {isInputVisible && (
          <div className="input-container">
            <input
              type="text"
              value={couponCode}
              onChange={(e) => setCouponCode(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Enter Coupon Code"
              className="input-field"
              disabled={isLoading}
            />
            <button 
              onClick={handleExclusive} 
              className="submit-button-submit"
              disabled={isLoading}
            >
              {isLoading ? "Submitting..." : "Submit"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductCard;